import { ComponentIds, HierarchyUtils, HostConnectionsComponent, } from "@inter-ikea-core/core";
import { copyPositionAndApplyFromOtherEntity, createSnapAction, } from "../functions/MountingRails";
import { MOUNTING_RAIL_TO_UPRIGHT, MOUNTING_RAIL_TO_UPRIGHT_LEFT, MOUNTING_RAIL_TO_WALL, UPRIGHT_TO_MOUNTING_RAIL, UPRIGHT_TO_MOUNTING_RAIL_600, UPRIGHT_TO_MOUNTING_RAIL_800, UPRIGHT_TO_MOUNTING_RAIL_ADJUSTABLE_0, UPRIGHT_TO_MOUNTING_RAIL_ADJUSTABLE_1, UPRIGHT_TO_MOUNTING_RAIL_ADJUSTABLE_10, UPRIGHT_TO_MOUNTING_RAIL_ADJUSTABLE_2, UPRIGHT_TO_MOUNTING_RAIL_ADJUSTABLE_3, UPRIGHT_TO_MOUNTING_RAIL_ADJUSTABLE_4, UPRIGHT_TO_MOUNTING_RAIL_ADJUSTABLE_5, UPRIGHT_TO_MOUNTING_RAIL_ADJUSTABLE_6, UPRIGHT_TO_MOUNTING_RAIL_ADJUSTABLE_7, UPRIGHT_TO_MOUNTING_RAIL_ADJUSTABLE_8, UPRIGHT_TO_MOUNTING_RAIL_ADJUSTABLE_9, UPRIGHT_TO_MOUNTING_RAIL_LEFT, } from "../constants/connectorKeys";
import { COMPONENT_IDS } from "@inter-ikea-storagetwo/range/dist/storage2ComponentIDs";
import { getConnectorsComponent, getHostConnections, } from "@inter-ikea-storagetwo/range/dist/functions/componentGetters";
import { CANT_CREATE_ENTITY, CHILD_IS_ORPHAN, CONNECTOR_DOESNT_EXISTS, NO_CONNECTION, } from "@inter-ikea-storagetwo/range/dist/constants/errors";
import { BACK_WALL, FRONT_WALL, LEFT_WALL, RIGHT_WALL, ROOM_SURFACE, } from "@inter-ikea-storagetwo/range/dist/constants/connectorKeys";
const groupedConnectorMap = {
    [UPRIGHT_TO_MOUNTING_RAIL]: [
        UPRIGHT_TO_MOUNTING_RAIL_ADJUSTABLE_0,
        UPRIGHT_TO_MOUNTING_RAIL_ADJUSTABLE_1,
        UPRIGHT_TO_MOUNTING_RAIL_ADJUSTABLE_2,
        UPRIGHT_TO_MOUNTING_RAIL_ADJUSTABLE_3,
        UPRIGHT_TO_MOUNTING_RAIL_ADJUSTABLE_4,
        UPRIGHT_TO_MOUNTING_RAIL_ADJUSTABLE_5,
        UPRIGHT_TO_MOUNTING_RAIL_ADJUSTABLE_6,
        UPRIGHT_TO_MOUNTING_RAIL_ADJUSTABLE_7,
        UPRIGHT_TO_MOUNTING_RAIL_ADJUSTABLE_8,
        UPRIGHT_TO_MOUNTING_RAIL_ADJUSTABLE_9,
        UPRIGHT_TO_MOUNTING_RAIL_ADJUSTABLE_10,
        UPRIGHT_TO_MOUNTING_RAIL_600,
        UPRIGHT_TO_MOUNTING_RAIL_800,
        UPRIGHT_TO_MOUNTING_RAIL_LEFT,
    ],
    [MOUNTING_RAIL_TO_UPRIGHT_LEFT]: [MOUNTING_RAIL_TO_UPRIGHT],
    [MOUNTING_RAIL_TO_WALL]: [
        FRONT_WALL,
        LEFT_WALL,
        RIGHT_WALL,
        BACK_WALL,
        ROOM_SURFACE,
    ],
    "to-bracket-right": ["right-attach-to-cloth-rail-connector"],
    "to-bracket-left": ["left-attach-to-cloth-rail-connector"],
};
const connectorMap = Object.fromEntries(Object.entries(groupedConnectorMap).flatMap(([value, keys]) => keys.map((key) => [key, value])));
export const MountingRailConnectionService = (core) => {
    const getHostKey = (connection) => connection.hostConnectorKey;
    const connect = (guest) => {
        return {
            to: to(guest),
        };
    };
    const shouldntMove = (entity) => entity.hasComponent(COMPONENT_IDS.PROHIBITED_REPLACE_COMPONENT);
    const withEntity = (entity) => (entityId) => {
        if (shouldntMove(entity))
            return;
        const newEntity = core.services.productManager.createEntity(entityId);
        if (!newEntity)
            throw new Error(CANT_CREATE_ENTITY);
        const toParentConnection = disconnect(entity);
        if (!toParentConnection)
            throw new Error(NO_CONNECTION);
        if (entity.hasComponent(COMPONENT_IDS.MOUNTING_RAIL_COMPONENT))
            copyPositionAndApplyFromOtherEntity(entity, newEntity);
        const children = HierarchyUtils.children(entity);
        connect(newEntity)
            .to(toParentConnection === null || toParentConnection === void 0 ? void 0 : toParentConnection.hostConnectorEntity)
            .atConnector(getHostKey(toParentConnection));
        children &&
            children.forEach((child) => !child.hasComponent(COMPONENT_IDS.PROHIBITED_REPLACE_COMPONENT) &&
                move(child).to(newEntity));
        return newEntity;
    };
    const replace = (entity) => {
        return {
            with: withEntity(entity),
        };
    };
    const disconnect = (guest) => {
        const host = HierarchyUtils.parent(guest);
        if (!host)
            throw new Error(CHILD_IS_ORPHAN);
        const hostConnections = getHostConnections(host);
        const processedConnections = hostConnections.reduce((acc, connection) => {
            var _a;
            if (connection.guestConnectorEntity.id !== guest.id)
                return Object.assign(Object.assign({}, acc), { filteredHostConnections: [
                        ...acc.filteredHostConnections,
                        connection,
                    ] });
            const removedConnection = acc.removedConnection &&
                ((_a = acc.removedConnection) === null || _a === void 0 ? void 0 : _a.guestConnector.priority) >
                    connection.guestConnector.priority
                ? acc.removedConnection
                : connection;
            return Object.assign(Object.assign({}, acc), { removedConnection: removedConnection });
        }, { filteredHostConnections: [], removedConnection: null });
        host.addComponent(new HostConnectionsComponent(processedConnections.filteredHostConnections));
        guest.removeComponent(ComponentIds.GUEST_CONNECTIONS_COMPONENT_ID);
        return processedConnections.removedConnection;
    };
    const to = (guest) => (host) => {
        return {
            atConnector: atConnector(guest, host),
        };
    };
    const toEntity = (guest) => (host) => {
        const oldConnection = disconnect(guest);
        if (!oldConnection)
            throw new Error(NO_CONNECTION);
        return connect(guest).to(host).atConnector(getHostKey(oldConnection));
    };
    const move = (guest) => {
        return {
            to: toEntity(guest),
        };
    };
    const atConnector = (guest, host) => (hostConnectorKey, allowDiscard = true) => {
        const guestConnectorKey = hostConnectorKey.includes("upright-to-insert-")
            ? "insert-to-upright-1"
            : connectorMap[hostConnectorKey];
        const hostConnector = getConnectorsComponent(host).getConnectorByKey(hostConnectorKey);
        const guestConnector = getConnectorsComponent(guest).getConnectorByKey(guestConnectorKey);
        if (!hostConnector || !guestConnector) {
            if (allowDiscard)
                return {
                    result: "child discarded",
                    reason: CONNECTOR_DOESNT_EXISTS,
                };
            throw new Error(CONNECTOR_DOESNT_EXISTS);
        }
        if (guest.hasComponent(ComponentIds.GUEST_CONNECTIONS_COMPONENT_ID)) {
            guest.removeComponent(ComponentIds.GUEST_CONNECTIONS_COMPONENT_ID);
        }
        const { errors, snap, valid } = createSnapAction(core, guest, guestConnector, host, hostConnector);
        if (!valid || errors.length) {
            console.error("valid: ", !valid, errors);
            return { valid, errors };
        }
        const result = snap.performMoveAndConnect();
        return { errors, valid, result };
    };
    return {
        connect,
        disconnect,
        move,
        replace,
    };
};
